import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";

const SUBTYPE_STYLES = {
  action: { bg: "from-blue-500 to-indigo-600", labelKey: "phaseActions.reveal.label.action", border: "border-blue-300" },
  event: { bg: "from-orange-500 to-orange-700", labelKey: "phaseActions.reveal.label.event", border: "border-orange-400" },
  market_melee: { bg: "from-red-500 to-rose-600", labelKey: "phaseActions.reveal.label.marketMelee", border: "border-red-300" },
};

const EFFECT_LOG_TYPES = ["effect", "play", "event", "melee", "draw", "buy", "buy_fail", "sell", "hold"];

const LOG_ENTRY_CLASS = {
  melee: "bg-red-50 text-red-700",
  event: "bg-amber-50 text-amber-700",
  draw: "bg-blue-50 text-blue-700",
  play: "bg-indigo-50 text-indigo-700",
  effect: "bg-indigo-50 text-indigo-700",
  buy: "bg-green-50 text-green-700",
  buy_fail: "bg-red-50 text-red-600",
  sell: "bg-orange-50 text-orange-700",
};

const RES_LABEL_KEYS = {
  holdable: "phaseActions.reveal.resolution.holdable",
  persistent: "phaseActions.reveal.resolution.persistent",
};

const RES_BADGE_CLASS = {
  holdable: "bg-blue-200 text-blue-800",
  persistent: "bg-purple-200 text-purple-800",
};

function RevealPhase({ game, onContinue, loading }) {
  const { t } = useTranslation();
  const drawnCard = game.last_drawn_card;
  const recentLogs = (game.event_log || []).slice(-6);
  const effectLogs = recentLogs.filter((e) => EFFECT_LOG_TYPES.includes(e.type));
  const cardStyle = drawnCard ? (SUBTYPE_STYLES[drawnCard.subtype] || SUBTYPE_STYLES.action) : SUBTYPE_STYLES.action;
  const cardLabel = t(cardStyle.labelKey);
  const resLabel = drawnCard && RES_LABEL_KEYS[drawnCard.resolutionType]
    ? t(RES_LABEL_KEYS[drawnCard.resolutionType])
    : t("phaseActions.reveal.resolved");
  const resBadgeClass = drawnCard ? (RES_BADGE_CLASS[drawnCard.resolutionType] || "bg-white/30 dark:bg-slate-900/30 text-white") : "bg-white/30 dark:bg-slate-900/30 text-white";

  return (
    <div className="text-center w-full max-w-lg" data-testid="reveal-phase">
      {drawnCard && (
        <div className={`bg-white dark:bg-slate-800 rounded-2xl border-2 ${cardStyle.border} shadow-xl overflow-hidden mb-4`} data-testid="drawn-card-display">
          <div className={`bg-gradient-to-r ${cardStyle.bg} px-4 py-2 flex items-center justify-between`}>
            <span className="text-sm font-bold text-white uppercase tracking-wider">{cardLabel}</span>
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${resBadgeClass}`}>{resLabel}</span>
          </div>
          <div className="p-4">
            <h3 className="text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-2" style={{ fontFamily: "Fredoka" }}>
              {drawnCard.name}
            </h3>
            <p className="text-sm text-[#1A237E]/80 dark:text-[#E3F2FD]/80 leading-relaxed">{drawnCard.description}</p>
          </div>
        </div>
      )}

      <div className="bg-white/80 dark:bg-slate-900/80 rounded-2xl border border-[#1A237E]/10 dark:border-[#E3F2FD]/10 p-3 mb-4 space-y-1.5 text-left">
        <p className="text-xs font-bold text-[#1A237E]/50 dark:text-[#E3F2FD]/50 uppercase tracking-wider mb-1">{t("phaseActions.reveal.whatHappened")}</p>
        {effectLogs.map((ev, i) => (
          <div
            key={`reveal-${ev.type}-${i}`}
            className={`text-xs px-3 py-1.5 rounded-lg ${LOG_ENTRY_CLASS[ev.type] || "bg-white text-[#1A237E]/60 dark:text-[#E3F2FD]/60"}`}
          >
            {ev.message}
          </div>
        ))}
      </div>

      <Button
        data-testid="continue-btn"
        className="action-btn rounded-full px-10 py-3 bg-[#38B6FF] hover:bg-[#2196F3] text-white text-lg font-bold shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px]"
        onClick={onContinue}
        disabled={loading}
      >
        <ChevronRight className="w-5 h-5 mr-2" /> {t("phaseActions.reveal.continue")}
      </Button>
    </div>
  );
}

export default memo(RevealPhase);
